import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, Check, X, Trash2 } from "lucide-react";

/**
 * Admin view of the private waiting list.
 * Zero-commitment queue captured by /waiting-list. Lets the compliance
 * team see the pipeline, mark people as invited when their project is
 * legally ready, and remove entries.
 */
export default function WaitingListAdmin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, waiting: 0, invited: 0 });
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/waiting-list");
      setRows(data.rows || []);
      setStats({ total: data.total, waiting: data.waiting, invited: data.invited });
    } catch (e) {
      toast.error("Could not load waiting list.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/waiting-list/${id}`, { status });
      setRows((xs) => xs.map((r) => r.id === id ? { ...r, status } : r));
      toast.success(`Marked ${status}.`);
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this person from the waiting list?")) return;
    try {
      await api.delete(`/admin/waiting-list/${id}`);
      setRows((xs) => xs.filter((r) => r.id !== id));
      toast.success("Removed.");
    } catch { toast.error("Delete failed."); }
  };

  return (
    <div className="mt-4" data-testid="admin-waiting-list">
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { i: <Users size={16}/>, k: "Total on list", v: stats.total },
          { i: <Loader2 size={16}/>, k: "Still waiting", v: stats.waiting },
          { i: <Check size={16}/>, k: "Invited", v: stats.invited },
        ].map((c) => (
          <div key={c.k} className="mv-card p-4">
            <div className="w-8 h-8 rounded-2xl flex items-center justify-center mv-green-text mb-1.5" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
            <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>{c.k}</div>
            <div className="font-display text-2xl mv-green-text mt-0.5">{busy ? "…" : c.v}</div>
          </div>
        ))}
      </div>

      <div className="mv-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left border-b mv-hairline bg-[var(--mv-bg)]" style={{ color: "var(--mv-muted)" }}>
                <th className="py-2.5 px-4 font-medium w-14">#</th>
                <th className="py-2.5 px-4 font-medium">Name</th>
                <th className="py-2.5 px-4 font-medium">Email</th>
                <th className="py-2.5 px-4 font-medium">Country</th>
                <th className="py-2.5 px-4 font-medium">Project</th>
                <th className="py-2.5 px-4 font-medium">WhatsApp</th>
                <th className="py-2.5 px-4 font-medium">Status</th>
                <th className="py-2.5 px-4 font-medium">Joined</th>
                <th className="py-2.5 px-4 font-medium w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={9} className="p-8 text-center"><Loader2 className="animate-spin mv-green-text inline"/></td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center" style={{ color: "var(--mv-muted)" }}>Nobody on the list yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b mv-hairline last:border-0 hover:bg-black/[0.015]" data-testid={`wl-row-${r.position}`}>
                  <td className="py-2.5 px-4 font-mono mv-green-text">#{r.position}</td>
                  <td className="py-2.5 px-4 font-semibold">{r.name}</td>
                  <td className="py-2.5 px-4"><a href={`mailto:${r.email}`} className="underline mv-green-text">{r.email}</a></td>
                  <td className="py-2.5 px-4">{r.country}</td>
                  <td className="py-2.5 px-4 text-[12.5px]">{r.project_of_interest || "—"}</td>
                  <td className="py-2.5 px-4 text-[12.5px]">
                    {r.whatsapp && r.whatsapp_opt_in
                      ? <a href={`https://wa.me/${r.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="underline mv-green-text">{r.whatsapp}</a>
                      : (r.whatsapp || "—")}
                  </td>
                  <td className="py-2.5 px-4">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                          style={r.status === "invited"
                            ? { background: "rgba(10,59,40,0.12)", color: "var(--mv-green)" }
                            : { background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-[12.5px]" style={{ color: "var(--mv-muted)" }}>
                    {r.created_at ? String(r.created_at).slice(0, 10) : "—"}
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-1.5">
                      {r.status === "waiting" ? (
                        <button onClick={() => setStatus(r.id, "invited")} title="Mark invited"
                                className="p-1.5 rounded-full hover:bg-black/5 mv-green-text"
                                data-testid={`wl-invite-${r.position}`}>
                          <Check size={14}/>
                        </button>
                      ) : (
                        <button onClick={() => setStatus(r.id, "waiting")} title="Move back to waiting"
                                className="p-1.5 rounded-full hover:bg-black/5" style={{ color: "var(--mv-muted)" }}
                                data-testid={`wl-back-${r.position}`}>
                          <X size={14}/>
                        </button>
                      )}
                      <button onClick={() => remove(r.id)} title="Remove"
                              className="p-1.5 rounded-full hover:bg-black/5" style={{ color: "#8a2020" }}
                              data-testid={`wl-delete-${r.position}`}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
